import LayoutSearch from "../components/LayoutSearch";
import css from "../css/listing/layout.module.css";

import Header from "../components/listing/Header";
import Breadcrumbs from "../components/listing/Breadcrumbs";

import Slider from "../components/listing/Slider";
import PropertyActive from "../components/listing/PropertyActive";
import PropertyArchive from "../components/listing/PropertyArchive";
import Snapshot from "../components/listing/Snapshot";
import Description from "../components/listing/Description";
import Features from "../components/listing/Features";
import MoreDetails from "../components/listing/MoreDetails";
import History from "../components/listing/History";
import AlternatePrices from "../components/listing/AlternatePrices";
import Expenses from "../components/listing/Expenses";
import Taxes from "../components/listing/Taxes";
import OpenHouses from "../components/listing/OpenHouses";
import Schools from "../components/listing/Schools";
import RightForm from "../components/listing/RightForm";
import GetMoreInfo from "../components/listing/GetMoreInfo";
import HeaderArchive from "../components/listing/HeaderArchive";

import React, { useEffect } from "react";
import Router from "next/router";
import Error from "../pages/_error";
import axiosResiInstance from "../http/axiosResi";
import * as resiPromise from "../http/resiPromise";
import { NextSeo } from "next-seo";
import Head from "next/head";
import { getSeoListing, getSeoListingKeywords } from "../service/seo";
import track from "../service/track";
import { sendAgentView } from "../service/agent-view";
import SearchContext from "../components/context/Search";
import isRent from "../helper/isRent";
import MapStatic from "../components/listing/MapStatic";
import useMobile from "../hooks/useMobile";
import NoSSR from "../components/common/NoSSR";
import useSticky from "../hooks/useSticky";
import Separator from "../components/common/Separator";
import { AGENT_ACTIVE_STATUS } from "../service/agent";
import {
    LISTHUB_SOURCE,
    LISTING_ACTIVE_STATUS,
    LISTING_ARCHIVE_STATUS, MLS_GRID_SOURCE
} from "../service/listing";

const fetchAgents = (listing) => {
    return axiosResiInstance.get(`/agents-get-more-info`, {
        params: {
            listing: listing.id,
        },
    });
};

const fetchSchools = (listingId) => {
    return axiosResiInstance.get("/listing-schools", {
        params: {
            listing: listingId,
            limit: 10,
        },
    });
};

const HEADER_HEIGHT = 57;

export const redirect = (url, res) => {
    const fullUrl = `/l/${url}`;

    if (res) {
        res.writeHead(301, {
            Location: fullUrl,
        });

        res.end();
    } else {
        Router.push(
            {
                pathname: `/listing`,
                query: {
                    listing: url,
                },
            },
            fullUrl
        );
    }
};

const Listing = (props) => {
    const { listing, agents, schools, errorCode } = props;

    const isMobile = useMobile();
    const scrolled = useSticky(HEADER_HEIGHT);

    useEffect(() => {
        if (listing) {
            track("listing.view", {
                id: listing.id,
            });
        }
    }, []);

    if (errorCode) {
        return <Error statusCode={errorCode} />;
    }

    const { primary_photo, photos } = listing;
    const isActive = listing.status === LISTING_ACTIVE_STATUS;
    const header = isActive ? (
        <Header listing={listing} />
    ) : (
        <HeaderArchive listing={listing} />
    );
    const keywords = getSeoListingKeywords(listing);
    const SEO = getSeoListing(listing, photos);

    const getMoreInfo = () => {
        if (!isActive || !agents || agents.length === 0) {
            return "";
        }

        return (
            <div className={css.stackGmo}>
                <div className={css.title2}>
                    Get more info on <b>{listing.full_address}</b>
                </div>
                <GetMoreInfo listing={listing} agents={agents} />
            </div>
        );
    };

    return (
        <SearchContext.Provider
            value={{
                activeSearchCategory: isRent(listing.category.name)
                    ? "rent"
                    : "sale",
            }}
        >
            <LayoutSearch>
                <NextSeo {...SEO} />
                {keywords && (
                    <Head>
                        <meta name="keywords" content={keywords} />
                    </Head>
                )}
                <div className={css.header}>
                    <Breadcrumbs
                        listing={listing}
                        showLogo={scrolled}
                        label={isActive ? "" : "Property"}
                    />
                    {header}
                </div>
                <div className={css.content}>
                    <div className={css.wrapper}>
                        <div className={css.columnLeft}>
                            {(primary_photo || photos) && (
                                <div className={css.stackLeftSlider}>
                                    <Slider listing={listing} />
                                </div>
                            )}
                            <div className={css.stackLeftProperties}>
                                {isActive && (
                                    <PropertyActive listing={listing} />
                                )}
                                {!isActive && (
                                    <PropertyArchive listing={listing} />
                                )}
                            </div>

                            <div className={css.stackLeft}>
                                <h2 className={css.title}>Snapshot</h2>
                                <Snapshot listing={listing} />
                            </div>

                            {isActive && listing.description && (
                                <>
                                    <Separator />
                                    <div className={css.stackLeft}>
                                        <h2 className={css.title}>
                                            Description
                                        </h2>
                                        <Description listing={listing} />
                                    </div>
                                </>
                            )}

                            {isActive && listing.listing_open_houses &&
                                listing.listing_open_houses.length > 0 && (
                                    <>
                                        <Separator />
                                        <div className={css.stackLeft}>
                                            <h2 className={css.title}>
                                                Open House Showings
                                            </h2>
                                            <OpenHouses listing={listing} />
                                        </div>
                                    </>
                                )}

                            {isActive && isMobile && getMoreInfo()}

                            {isActive &&
                                (listing.source === LISTHUB_SOURCE ||
                                    listing.source === MLS_GRID_SOURCE) && (
                                    <>
                                        <Separator />
                                        <div className={css.stackLeft}>
                                            <h2 className={css.title}>
                                                More Details
                                            </h2>
                                            <MoreDetails
                                                listing={listing}
                                                width={props.width}
                                            />
                                        </div>
                                    </>
                                )}

                            {isActive && (
                                <>
                                    <Separator />
                                    <div className={css.stackLeft}>
                                        <h2 className={css.title}>Features</h2>
                                        <Features data={listing} />
                                    </div>
                                </>
                            )}

                            {isActive && (
                                <>
                                    <Separator />
                                    <div className={css.stackLeft}>
                                        <h2 className={css.title}>History</h2>
                                        <History listing={listing} />
                                    </div>
                                </>
                            )}

                            {isActive && listing.alternate_prices &&
                                listing.alternate_prices.length > 0 && (
                                    <>
                                        <Separator />
                                        <div className={css.stackLeft}>
                                            <h2 className={css.title}>
                                                Alternate Prices
                                            </h2>
                                            <AlternatePrices listing={listing} />
                                        </div>
                                    </>
                                )}

                            {isActive && listing.expenses &&
                                listing.expenses.length > 0 && (
                                    <>
                                        <Separator />
                                        <div className={css.stackLeft}>
                                            <h2 className={css.title}>
                                                Expenses
                                            </h2>
                                            <Expenses listing={listing} />
                                        </div>
                                    </>
                                )}

                            {isActive &&
                                listing.taxes &&
                                listing.taxes.length > 0 && (
                                    <>
                                        <Separator />
                                        <div className={css.stackLeft}>
                                            <h2 className={css.title}>Taxes</h2>
                                            <Taxes listing={listing} />
                                        </div>
                                    </>
                                )}

                            {schools !== null && (
                                <>
                                    <Separator />
                                    <div className={css.stackLeft}>
                                        <h2 className={css.title}>
                                            Nearby Schools
                                        </h2>
                                        <Schools schools={schools} />
                                    </div>
                                </>
                            )}
                            {isActive && !isMobile && getMoreInfo()}
                        </div>
                        <div className={css.columnRight}>
                            {!!listing.latitude && !!listing.longitude && (
                                <div className={css.stackRight}>
                                    <NoSSR>
                                        <MapStatic listing={listing} />
                                    </NoSSR>
                                </div>
                            )}
                            {isActive &&
                                listing.agent &&
                                listing.agent.status ===
                                    AGENT_ACTIVE_STATUS && (
                                    <div className={css.stackRight}>
                                        <RightForm listing={listing} />
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
            </LayoutSearch>
        </SearchContext.Provider>
    );
};

Listing.getInitialProps = async ({ query, req, res }) => {
    let listing = null;
    let agents = null;
    let schools = null;
    let errorCode = null;

    try {
        const response = await axiosResiInstance(
            `/listings/${query["listing"]}`
        );
        listing = response.data;
    } catch (e) {
        errorCode = 404;

        if (res) {
            res.statusCode = errorCode;
        }
    }

    if (
        listing &&
        listing.status === LISTING_ARCHIVE_STATUS &&
        listing.redirect
    ) {
        redirect(listing.redirect.url, res);
    }

    // redirect listing to agent's version
    if (listing && query["listing"] !== listing.url) {
        redirect(listing.url, res);
    }

    if (listing) {
        let promises = [];

        promises.push(fetchSchools(listing.id));

        if (listing.status === LISTING_ACTIVE_STATUS) {
            promises.push(fetchAgents(listing));

            if (listing.agent && listing.agent.status === AGENT_ACTIVE_STATUS) {
                promises.push(sendAgentView(listing.agent.id, "LISTING_PAGE"));
            }
        }

        const response = await resiPromise.allSettledReported(
            promises,
            "pages/listing.js",
            req
        );

        schools = response[0] ? response[0].data.data : null;
        agents = response[1] ? response[1].data : null;
    }

    return {
        errorCode,
        listing,
        agents,
        schools,
    };
};

export default Listing;
